import React from 'react'
import { Calendar } from 'lucide-react'
import { formatDate } from '../../helpers'

interface IDateTimePickerProps {
  // Date
  selectedDate: Date | null
  onDateSelect: (date: Date) => void
  showCalendar: boolean
  onShowCalendarChange: (show: boolean) => void
  currentCalendarDate: Date
  onCurrentCalendarDateChange: (date: Date) => void

  // Start Time
  startTime: string
  onStartTimeSelect: (time: string) => void
  showStartTimePicker: boolean
  onShowStartTimePickerChange: (show: boolean) => void
  displayedStartTime: string
  validStartTimes: { value: string; display: string }[]

  // End Time
  endTime: string
  onEndTimeSelect: (time: string) => void
  showEndTimePicker: boolean
  onShowEndTimePickerChange: (show: boolean) => void
  displayedEndTime: string
  validEndTimes: { value: string; display: string }[]

  // Refs
  calendarRef: React.RefObject<HTMLDivElement>
  startTimeRef: React.RefObject<HTMLDivElement>
  endTimeRef: React.RefObject<HTMLDivElement>
}

// Calendar Picker Component
const CalendarPicker: React.FC<{
  selectedDate: Date | null
  onDateSelect: (date: Date) => void
  currentCalendarDate: Date
  onCurrentCalendarDateChange: (date: Date) => void
  calendarRef: React.RefObject<HTMLDivElement>
}> = ({
  selectedDate,
  onDateSelect,
  currentCalendarDate,
  onCurrentCalendarDateChange,
  calendarRef,
}) => {
  const today = new Date()
  const currentMonth = currentCalendarDate.getMonth()
  const currentYear = currentCalendarDate.getFullYear()

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate()
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay()

  const days: (Date | null)[] = []

  // Empty cells for days before the first day of the month
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(null)
  }

  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(new Date(currentYear, currentMonth, day))
  }

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  const goToPreviousMonth = () => {
    onCurrentCalendarDateChange(new Date(currentYear, currentMonth - 1, 1))
  }

  const goToNextMonth = () => {
    onCurrentCalendarDateChange(new Date(currentYear, currentMonth + 1, 1))
  }

  return (
    <div
      ref={calendarRef}
      className="absolute top-full left-0 mt-1 bg-white border border-primary-outline rounded-lg shadow-lg z-50 p-4 w-80"
    >
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={goToPreviousMonth}
          className="p-1 rounded-md hover:bg-primary-brandLight text-primary-text"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        <h3 className="text-lg font-semibold text-primary-text">
          {monthNames[currentMonth]} {currentYear}
        </h3>

        <button
          onClick={goToNextMonth}
          className="p-1 rounded-md hover:bg-primary-brandLight text-primary-text"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Day names */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayNames.map((day) => (
          <div key={day} className="text-center text-xs font-medium text-silver-light py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((date, index) => {
          if (!date) {
            return <div key={index} className="h-8"></div>
          }

          const isToday = date.toDateString() === today.toDateString()
          const isPast = date < today && !isToday
          const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString()

          return (
            <button
              key={index}
              onClick={() => !isPast && onDateSelect(date)}
              disabled={isPast}
              className={`
                h-8 w-8 text-sm rounded-md flex items-center justify-center
                ${
                  isPast
                    ? 'text-silver-light cursor-not-allowed opacity-40'
                    : 'text-primary-text hover:bg-primary-brandLight cursor-pointer'
                }
                ${isToday ? 'bg-primary text-white font-semibold' : ''}
                ${isSelected && !isToday ? 'bg-primary-brandLight border border-primary' : ''}
              `}
            >
              {date.getDate()}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// Time Picker Component
const TimePicker = React.forwardRef<
  HTMLDivElement,
  {
    timeSlots: { value: string; display: string }[]
    onTimeSelect: (time: string) => void
    selectedTime: string
  }
>(({ timeSlots, onTimeSelect, selectedTime }, ref) => (
  <div
    ref={ref}
    className="absolute top-full left-0 mt-1 bg-white border border-primary-outline rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto w-full"
  >
    {timeSlots.map((slot) => (
      <button
        key={slot.value}
        onClick={() => onTimeSelect(slot.value)}
        className={`w-full text-left px-3 py-2 text-sm hover:bg-primary-brandLight ${
          selectedTime === slot.value ? 'bg-primary-brandLight text-primary' : 'text-primary-text'
        }`}
      >
        {slot.display}
      </button>
    ))}
  </div>
))

// Main DateTimePicker Component
export const DateTimePicker: React.FC<IDateTimePickerProps> = ({
  selectedDate,
  onDateSelect,
  showCalendar,
  onShowCalendarChange,
  currentCalendarDate,
  onCurrentCalendarDateChange,
  startTime,
  onStartTimeSelect,
  showStartTimePicker,
  onShowStartTimePickerChange,
  displayedStartTime,
  validStartTimes,
  endTime,
  onEndTimeSelect,
  showEndTimePicker,
  onShowEndTimePickerChange,
  displayedEndTime,
  validEndTimes,
  calendarRef,
  startTimeRef,
  endTimeRef,
}) => {
  return (
    <div className="flex items-center gap-3 mb-5">
      {/* Date Picker */}
      <div className="flex-1 relative">
        <div
          className="flex items-center border border-primary-outline rounded-md px-3 py-2 cursor-pointer"
          onClick={() => {
            onShowCalendarChange(!showCalendar)
            onShowStartTimePickerChange(false)
            onShowEndTimePickerChange(false)
          }}
        >
          <Calendar className="w-4 h-4 mr-2 opacity-60" />
          <span
            className={`flex-1 text-sm whitespace-nowrap ${
              selectedDate ? 'text-primary-text' : 'text-silver-light opacity-60'
            }`}
          >
            {selectedDate ? formatDate(selectedDate) : 'Pick a date'}
          </span>
        </div>
        {showCalendar && (
          <CalendarPicker
            selectedDate={selectedDate}
            onDateSelect={onDateSelect}
            currentCalendarDate={currentCalendarDate}
            onCurrentCalendarDateChange={onCurrentCalendarDateChange}
            calendarRef={calendarRef}
          />
        )}
      </div>

      {/* Start Time Picker */}
      <div className="w-[120px] relative">
        <div
          className="w-full rounded-md border border-primary-outline px-3 py-2 text-sm text-center focus:border-primary focus:ring-primary focus:outline-none cursor-pointer"
          onClick={() => {
            onShowStartTimePickerChange(!showStartTimePicker)
            onShowCalendarChange(false)
            onShowEndTimePickerChange(false)
          }}
          title="Start Time"
        >
          {displayedStartTime}
        </div>
        {showStartTimePicker && (
          <TimePicker
            timeSlots={validStartTimes}
            onTimeSelect={onStartTimeSelect}
            selectedTime={startTime}
            ref={startTimeRef}
          />
        )}
      </div>

      <span className="text-silver-light">–</span>

      {/* End Time Picker */}
      <div className="w-[120px] relative">
        <div
          className="w-full rounded-md border border-primary-outline px-3 py-2 text-sm text-center focus:border-primary focus:ring-primary focus:outline-none cursor-pointer"
          onClick={() => {
            onShowEndTimePickerChange(!showEndTimePicker)
            onShowCalendarChange(false)
            onShowStartTimePickerChange(false)
          }}
          title="End Time"
        >
          {displayedEndTime}
        </div>
        {showEndTimePicker && (
          <TimePicker
            timeSlots={validEndTimes}
            onTimeSelect={onEndTimeSelect}
            selectedTime={endTime}
            ref={endTimeRef}
          />
        )}
      </div>
    </div>
  )
}
